import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Box, Button, CircularProgress, Modal } from "@mui/material";
import { Close } from "@mui/icons-material";
import Dropzone from "react-dropzone";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import FileUploadIcon from "@mui/icons-material/FileUpload";

import api from "services/api";
import { useAlert } from "contexts/alert";
import layoutBeneficiariosUpload from "assets/documents/layout_beneficiarios_upload.xlsx?url";

import { IUploadIssue, IUploadResult } from "./Governance.types";
import { UploadContainer } from "./Governance.styled";

type GovernanceUploadModalProps = {
  organizationId: string;
  isOpen: boolean;
  onClose: () => void;
};

export function GovernanceUploadModal({ organizationId, isOpen, onClose }: GovernanceUploadModalProps) {
  const { openAlert } = useAlert();
  const queryClient = useQueryClient();

  const [isUploading, setIsUploading] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  /** Não-nulo coloca o modal na terceira tela, a das críticas. */
  const [issues, setIssues] = useState<IUploadIssue[] | null>(null);

  /** Volta ao estado zerado: sem arquivo e sem críticas, exigindo novo upload. */
  function resetToUpload() {
    setFile(null);
    setIssues(null);
  }

  function handleClose() {
    resetToUpload();
    onClose();
  }

  function handleDownloadTemplate() {
    const link = document.createElement("a");
    link.href = layoutBeneficiariosUpload;
    link.download = "layout_beneficiarios_upload.xlsx";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  async function handleUpload() {
    try {
      if (!file) throw new Error("File is mandatory.");

      setIsUploading(true);

      const formData = new FormData();
      formData.append("file", file as Blob);

      const response = await api.post<{ data: IUploadResult }>(
        `/v2/governance/${organizationId}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      const result = response.data.data;

      /**
       * O endpoint responde 200 mesmo rejeitando o arquivo, então sem checar `valido` a tela
       * diria "sucesso" para um upload que não gravou nada.
       */
      if (!result.valido) {
        setIssues(result.erros);

        return;
      }

      openAlert("Arquivo carregado com sucesso.", "success");
      await queryClient.invalidateQueries({ queryKey: ["governance-upload-history", organizationId] });
      handleClose();
    } catch (error) {
      //@ts-ignore
      if (error?.response?.data?.data?.message) {
        //@ts-ignore
        openAlert(error?.response?.data?.data?.message, "error");
      } else {
        openAlert("Houve algum erro ao enviar o arquivo.", "error");
      }

      /** Erro de API fecha o modal — não há nada para o usuário corrigir aqui. */
      handleClose();
    } finally {
      setIsUploading(false);
    }
  }

  return (
    <Modal open={isOpen} aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description">
      <Box sx={{ position: "absolute" as "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", height: "auto", width: "100%", maxWidth: 900, bgcolor: "background.paper", boxShadow: 24, borderRadius: "1rem", border: "none", outline: "none", padding: "24px" }}>
        <div style={{"display":"flex","justifyContent":"space-between","alignItems":"center"}}>
          <h6 style={{"padding":"0","margin":"0","fontSize":"1.25rem","lineHeight":"1.75rem","fontWeight":600}}>
            {issues ? "Críticas encontradas" : "Carregar Beneficiários"}
          </h6>
          <Close style={{"cursor":"pointer"}} onClick={handleClose} />
        </div>

        {issues ? (
          <>
            <div style={{ display: "flex", flexDirection: "column", gap: "16px", marginTop: "24px" }}>
              <span style={{ fontSize: "16px", lineHeight: "175%", fontWeight: "400", color: "#191C1A" }}>
                O arquivo foi rejeitado por completo e nada foi gravado. Corrija {issues.length === 1 ? "a crítica" : `as ${issues.length} críticas`} abaixo e envie a planilha novamente.
              </span>

              <div style={{ maxHeight: "360px", overflowY: "auto", border: "solid 1px #E8E8E8", borderRadius: "16px" }}>
                {issues.map((issue, index) => (
                  <div
                    key={`${issue.linha}-${issue.campo}-${index}`}
                    style={{ display: "flex", gap: "16px", padding: "12px 16px", borderBottom: index === issues.length - 1 ? "none" : "solid 1px #F2F2F2" }}
                  >
                    <span style={{ fontSize: "13px", fontWeight: 600, color: "#191C1A", minWidth: "70px" }}>
                      Linha {issue.linha}
                    </span>
                    <span style={{ fontSize: "13px", fontWeight: 600, color: "#6F6F78", minWidth: "220px" }}>
                      {issue.campo}
                    </span>
                    <span style={{ fontSize: "13px", color: "#6F6F78" }}>{issue.mensagem}</span>
                  </div>
                ))}
              </div>
            </div>

            <div style={{"display":"flex","paddingTop":"0.5rem","paddingBottom":"0.5rem","marginTop":"1rem","justifyContent":"flex-end","alignItems":"center","width":"100%"}}>
              <Button
                variant="contained"
                style={{ minWidth: "180px", "paddingTop":"0.25rem","paddingBottom":"0.25rem","paddingLeft":"1rem","paddingRight":"1rem","borderRadius":"0.375rem","height":"2.5rem","fontSize":"0.875rem","lineHeight":"1.25rem","fontWeight":600}}
                onClick={resetToUpload}
              >
                Voltar
              </Button>
            </div>
          </>
        ) : (
          <>
            <div style={{ display: "flex", flexDirection: "column", gap: "24px", marginTop: "24px" }}>
              <span style={{ fontSize: "16px", lineHeight: "175%", fontWeight: "400", color: "#191C1A" }}>
                Faça upload de um arquivo Excel (
                <span
                  onClick={handleDownloadTemplate}
                  style={{
                    color: "#1976d2",
                    cursor: "pointer",
                    textDecoration: "underline",
                    fontWeight: "500",
                  }}
                >
                  use este template
                </span>
                ) para adicionar ou atualizar beneficiários da base total de vidas. O template traz uma aba com o guia de preenchimento de cada coluna.
              </span>

              {!!file && (
                <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "8px" }}>
                  <InsertDriveFileOutlinedIcon style={{ width: "24px" }} />
                  <span style={{ fontSize: "13px", fontWeight: "600", color: "#191C1A" }}>{file.name}</span>
                  <DeleteIcon color="error" style={{ width: "24px", cursor: "pointer" }} onClick={() => setFile(null)} />
                </div>
              )}

              {!file && (
                <Dropzone
                  onDrop={(acceptedFiles) => (acceptedFiles.length ? setFile(acceptedFiles[0]) : setFile(null))}
                  accept={{
                    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
                  }}
                >
                  {({ getRootProps, getInputProps, isDragActive }) => (
                    <div className="container" style={{ marginTop: "-24px" }}>
                      <UploadContainer {...getRootProps({ className: "dropzone" })}>
                        <input {...getInputProps()} />
                        <FileUploadIcon style={{ fontSize: "24px", color: "#3024E1" }} />
                        {isDragActive ? (
                          <>
                            <p style={{ color: "#3024E1", fontSize: "16px", margin: "0" }}>Solte o arquivo aqui…</p>
                            <p style={{ fontSize: "12px", margin: "0", color: "#0F0F1E99" }}>Liberando para upload</p>
                          </>
                        ) : (
                          <>
                            <p style={{ color: "#3024E1", fontSize: "16px", margin: "0" }}>Arraste e solte ou clique para procurar</p>
                            <p style={{ fontSize: "12px", margin: "0", color: "#0F0F1E99" }}>Somente arquivos no formato xlsx são aceitos</p>
                          </>
                        )}
                      </UploadContainer>
                    </div>
                  )}
                </Dropzone>
              )}
            </div>

            <div style={{"display":"flex","paddingTop":"0.5rem","paddingBottom":"0.5rem","marginTop":"1rem","justifyContent":"flex-end","alignItems":"center","width":"100%"}}>
              <div style={{"display":"flex","gap":"0.25rem"}}>
                <Button
                  variant="outlined"
                  color="error"
                  style={{minWidth: "180px","paddingTop":"0.25rem","paddingBottom":"0.25rem","paddingLeft":"1rem","paddingRight":"1rem","borderRadius":"0.375rem","height":"2.5rem","fontSize":"0.875rem","lineHeight":"1.25rem","fontWeight":600}}
                  onClick={handleClose}
                  disabled={isUploading}
                >
                  Cancelar
                </Button>

                <Button
                  variant="contained"
                  style={{ minWidth: "180px", "paddingTop":"0.25rem","paddingBottom":"0.25rem","paddingLeft":"1rem","paddingRight":"1rem","borderRadius":"0.375rem","height":"2.5rem","fontSize":"0.875rem","lineHeight":"1.25rem","fontWeight":600}}
                  onClick={handleUpload}
                  disabled={isUploading || !file}
                >
                  {!isUploading ? "Carregar" : <CircularProgress size="20px" />}
                </Button>
              </div>
            </div>
          </>
        )}
      </Box>
    </Modal>
  );
}
